import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import {
	emit,
	markInteraction,
	resetPageMetrics,
	secondsOnPage,
	session,
	setLocaleContext,
	trackClientError,
	trackContact,
	trackCopy,
	trackCtaClick,
	trackDeadClick,
	trackDownload,
	trackExitIntent,
	trackFirstScroll,
	trackFormView,
	trackIdle,
	trackNavClick,
	trackOutbound,
	trackPageLeave,
	trackPageRestored,
	trackPageView,
	trackPrint,
	trackRageClick,
	trackRegisterIntent,
	trackSchedule,
	trackScrollBack,
	trackScrollDepth,
	trackSectionDwell,
	trackSectionView,
	trackSessionStart,
	trackTabHidden,
	trackTabReturn,
	trackTimeMilestone,
	trackViewContent,
} from "#/lib/analytics";
import { useI18n } from "#/lib/i18n";

const SCROLL_MARKS = [10, 25, 50, 75, 90, 100];
const TIME_MARKS = [5, 15, 30, 60, 120, 180, 300];
/** No interaction for this long (while the tab is visible) counts as idle. */
const IDLE_MS = 45_000;
/** Interaction older than this stops the engaged-time clock. */
const ENGAGED_WINDOW_MS = 30_000;

function labelOf(el: Element): string {
	const node = el as HTMLElement;
	return (
		node.getAttribute("data-track-label") ??
		node.getAttribute("aria-label") ??
		node.textContent?.trim().replace(/\s+/g, " ").slice(0, 80) ??
		node.tagName.toLowerCase()
	);
}

/** Roughly where on the page the element sits — above the fold, mid, bottom. */
function positionOf(el: Element): string {
	const top = el.getBoundingClientRect().top + window.scrollY;
	const height = document.documentElement.scrollHeight || 1;
	const ratio = top / height;
	return ratio < 0.2 ? "top" : ratio < 0.7 ? "middle" : "bottom";
}

/**
 * Every passive signal the landing page can produce: scroll depth, reading
 * time, engaged time, clicks (declared and inferred), friction, exit intent
 * and how the visit ended.
 *
 * Mounted once in the root document so it also covers secondary routes.
 * Section-level tracking lives in `useSectionTracking`, next to the page that
 * declares the sections.
 */
export function PageAnalytics() {
	const { locale } = useI18n();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const firstPath = useRef(true);

	// Keep the locale on every event payload in sync with the switcher.
	useEffect(() => {
		setLocaleContext(locale);
	}, [locale]);

	// Session bootstrap + client-side route changes. `locale` is read but not a
	// dependency on purpose: switching language must not re-fire a page view.
	// biome-ignore lint/correctness/useExhaustiveDependencies: see above
	useEffect(() => {
		if (firstPath.current) {
			firstPath.current = false;
			trackSessionStart(pathname, locale);
			return;
		}
		// A SPA navigation: close out the previous page, then start a new one.
		trackPageLeave("route_change");
		resetPageMetrics(pathname);
		trackPageView(pathname);
	}, [pathname]);

	// Scroll depth + direction.
	useEffect(() => {
		let ticking = false;
		let scrolled = false;
		let lastY = window.scrollY;
		let backReported = false;

		/**
		 * `silent` is used for the very first measurement: whatever is visible on
		 * load is a property of the viewport, not of engagement, so it seeds the
		 * baseline without reporting a scroll milestone.
		 */
		const measure = (silent = false) => {
			ticking = false;
			const docHeight = document.documentElement.scrollHeight;
			const seen = window.scrollY + window.innerHeight;
			const depth = Math.max(
				0,
				Math.min(100, Math.round((seen / Math.max(docHeight, 1)) * 100)),
			);

			if (depth > session.maxScroll) {
				session.maxScroll = depth;
				for (const mark of SCROLL_MARKS) {
					if (depth >= mark && !session.scrollMarks.has(mark)) {
						session.scrollMarks.add(mark);
						if (silent) continue;
						trackScrollDepth(mark);
						// Half the page read is a solid "consumed the offer" signal.
						if (mark === 50) trackViewContent("scroll_50");
					}
				}
			} else if (
				!backReported &&
				session.maxScroll - depth >= 25 &&
				session.maxScroll >= 50
			) {
				// Scrolled a long way back up — usually re-reading before deciding.
				backReported = true;
				trackScrollBack(session.maxScroll, depth);
			}
		};

		const onScroll = () => {
			markInteraction("scroll");
			if (!scrolled) {
				scrolled = true;
				trackFirstScroll(window.scrollY >= lastY ? "down" : "up");
			}
			lastY = window.scrollY;
			if (ticking) return;
			ticking = true;
			// Wrapped: rAF passes a timestamp, which would land in `silent`.
			requestAnimationFrame(() => measure());
		};

		measure(true);
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	// Engaged time, reading milestones and idle detection.
	useEffect(() => {
		const marks = new Set<number>();
		let idleReported = false;

		const tick = window.setInterval(() => {
			const visible = document.visibilityState === "visible";
			const sinceActivity =
				(typeof performance !== "undefined" ? performance.now() : 0) -
				session.lastActivityAt;

			if (visible && sinceActivity < ENGAGED_WINDOW_MS) {
				session.activeMs += 1000;
				idleReported = false;
			} else if (visible && sinceActivity >= IDLE_MS && !idleReported) {
				idleReported = true;
				trackIdle(Math.round(sinceActivity / 1000));
			}

			const elapsed = secondsOnPage();
			for (const mark of TIME_MARKS) {
				if (elapsed >= mark && !marks.has(mark)) {
					marks.add(mark);
					trackTimeMilestone(mark);
				}
			}
		}, 1000);

		return () => window.clearInterval(tick);
	}, []);

	// Anything the visitor does — feeds engaged time and "first interaction".
	useEffect(() => {
		const activity = () => markInteraction("input");
		const events: Array<keyof WindowEventMap> = [
			"pointerdown",
			"keydown",
			"touchstart",
			"wheel",
		];
		for (const name of events) {
			window.addEventListener(name, activity, { passive: true });
		}
		return () => {
			for (const name of events) window.removeEventListener(name, activity);
		};
	}, []);

	// Click tracking: declared (`data-track`), inferred (links), and friction
	// (rage / dead clicks) — all from one delegated listener.
	useEffect(() => {
		let lastClickAt = 0;
		let lastX = 0;
		let lastY = 0;
		let streak = 0;
		let rageReportedAt = 0;
		let deadClicks = 0;

		const onClick = (event: MouseEvent) => {
			const target = event.target as Element | null;
			if (!target) return;
			session.clicks += 1;

			// --- rage click ---------------------------------------------------
			const stamp = event.timeStamp;
			const near =
				Math.abs(event.clientX - lastX) < 40 &&
				Math.abs(event.clientY - lastY) < 40;
			streak = stamp - lastClickAt < 800 && near ? streak + 1 : 1;
			lastClickAt = stamp;
			lastX = event.clientX;
			lastY = event.clientY;
			if (streak >= 3 && stamp - rageReportedAt > 5000) {
				rageReportedAt = stamp;
				trackRageClick(labelOf(target), streak);
			}

			// --- declared CTA ---------------------------------------------------
			const tracked = target.closest<HTMLElement>("[data-track]");
			if (tracked) {
				const id = tracked.dataset.track ?? "unknown";
				const type = tracked.dataset.trackType ?? "cta";
				const section = tracked.dataset.trackSection;
				const href = tracked.getAttribute("href") ?? undefined;
				const label = labelOf(tracked);

				trackCtaClick({
					id,
					type,
					label,
					section,
					href,
					position: positionOf(tracked),
				});

				if (type === "download" && href) {
					trackDownload(href, tracked.dataset.trackLocale ?? "unknown");
				}
				if (type === "contact") trackContact(id, positionOf(tracked));
				if (type === "schedule") trackSchedule(id);
				if (type === "nav") trackNavClick(href ?? id, label);
				if (href?.startsWith("#register")) {
					trackRegisterIntent({ id, type, label, section });
				}
				return;
			}

			// --- undeclared link: classify it anyway -----------------------------
			const link = target.closest<HTMLAnchorElement>("a[href]");
			if (link) {
				const href = link.getAttribute("href") ?? "";
				const label = labelOf(link);
				if (link.hasAttribute("download") || href.endsWith(".pdf")) {
					trackDownload(href, "unknown");
				} else if (href.startsWith("tel:")) {
					trackContact("phone");
				} else if (href.startsWith("mailto:")) {
					trackContact("email");
				} else if (href.startsWith("#")) {
					trackNavClick(href, label);
				} else if (
					link.hostname &&
					link.hostname !== window.location.hostname
				) {
					trackOutbound(href, label);
				} else {
					trackNavClick(href, label);
				}
				return;
			}

			// Form controls report through the form funnel (focus / complete /
			// submit) — a raw click on them would only duplicate that.
			if (target.closest("input, select, textarea, label")) return;

			// --- buttons and everything else -------------------------------------
			const button = target.closest<HTMLElement>(
				"button, [role='button'], summary",
			);
			if (button) {
				emit("ui_click", {
					params: {
						element: button.tagName.toLowerCase(),
						element_label: labelOf(button),
					},
				});
				return;
			}

			// Nothing interactive under the pointer — a click into the void.
			// Capped so a visitor drumming on the page can't flood the pixel.
			if (deadClicks++ < 5) trackDeadClick(labelOf(target));
		};

		document.addEventListener("click", onClick, { capture: true });
		return () =>
			document.removeEventListener("click", onClick, { capture: true });
	}, []);

	// Exit intent — pointer leaves through the top of the viewport (desktop only).
	useEffect(() => {
		if (
			typeof window.matchMedia === "function" &&
			window.matchMedia("(pointer: coarse)").matches
		) {
			return;
		}
		let fired = false;
		const onOut = (event: MouseEvent) => {
			if (fired || event.relatedTarget || event.clientY > 8) return;
			fired = true;
			trackExitIntent();
		};
		document.addEventListener("mouseout", onOut);
		return () => document.removeEventListener("mouseout", onOut);
	}, []);

	// Leaving: tab switch, close, navigation away, bfcache restore.
	useEffect(() => {
		let hiddenAt = 0;

		const onVisibility = () => {
			if (document.visibilityState === "hidden") {
				hiddenAt = Date.now();
				trackTabHidden();
				// The only leave signal that reliably survives a mobile app switch
				// or tab close — everything is flushed here.
				trackPageLeave("hidden");
			} else {
				trackTabReturn(
					hiddenAt ? Math.round((Date.now() - hiddenAt) / 1000) : 0,
				);
				// Allow a final leave event if the visitor really goes away later.
				session.leaveSent = false;
			}
		};
		const onPageHide = () => trackPageLeave("pagehide");
		const onPageShow = (event: PageTransitionEvent) => {
			if (!event.persisted) return;
			session.leaveSent = false;
			trackPageRestored();
		};

		document.addEventListener("visibilitychange", onVisibility);
		window.addEventListener("pagehide", onPageHide);
		window.addEventListener("pageshow", onPageShow);
		return () => {
			document.removeEventListener("visibilitychange", onVisibility);
			window.removeEventListener("pagehide", onPageHide);
			window.removeEventListener("pageshow", onPageShow);
		};
	}, []);

	// Copy, print and client-side errors.
	useEffect(() => {
		const onCopy = () => {
			const text = window.getSelection()?.toString() ?? "";
			if (!text) return;
			trackCopy(text.length, text.slice(0, 60));
		};
		const onPrint = () => trackPrint();

		let errors = 0;
		const onError = (event: ErrorEvent) => {
			if (errors++ > 2) return;
			trackClientError(event.message, event.filename ?? "window");
		};
		const onRejection = (event: PromiseRejectionEvent) => {
			if (errors++ > 2) return;
			trackClientError(String(event.reason).slice(0, 100), "promise");
		};

		document.addEventListener("copy", onCopy);
		window.addEventListener("beforeprint", onPrint);
		window.addEventListener("error", onError);
		window.addEventListener("unhandledrejection", onRejection);
		return () => {
			document.removeEventListener("copy", onCopy);
			window.removeEventListener("beforeprint", onPrint);
			window.removeEventListener("error", onError);
			window.removeEventListener("unhandledrejection", onRejection);
		};
	}, []);

	return null;
}

/**
 * Reports which sections were reached, in what order, and how long each held
 * attention. Reads every `[data-section]` element on the page, so adding a new
 * section to the markup is enough to start tracking it.
 */
export function useSectionTracking() {
	useEffect(() => {
		const nodes = Array.from(
			document.querySelectorAll<HTMLElement>("[data-section]"),
		);
		if (nodes.length === 0) return;

		const enteredAt = new Map<string, number>();

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					const el = entry.target as HTMLElement;
					const name = el.dataset.section ?? "unknown";
					const index = nodes.indexOf(el) + 1;
					// Tall sections never reach a high ratio — treat "fills a good
					// part of the viewport" as seen too.
					const seen =
						entry.isIntersecting &&
						(entry.intersectionRatio >= 0.3 ||
							entry.intersectionRect.height >= window.innerHeight * 0.4);

					if (seen) {
						if (!enteredAt.has(name)) enteredAt.set(name, Date.now());
						if (!session.sections.includes(name)) {
							session.sections.push(name);
							session.deepestSection = name;
							trackSectionView(name, index);
							// The offer section is the strongest ViewContent trigger.
							if (name === "course") trackViewContent("course");
							if (name === "register") trackFormView("landing-register");
						}
						continue;
					}

					const start = enteredAt.get(name);
					if (start) {
						enteredAt.delete(name);
						const dwell = Math.round((Date.now() - start) / 1000);
						if (dwell >= 2) trackSectionDwell(name, dwell);
					}
				}
			},
			{ threshold: [0, 0.3, 0.6] },
		);

		for (const node of nodes) observer.observe(node);

		// Flush open dwells when the page goes away.
		const flush = () => {
			for (const [name, start] of enteredAt) {
				const dwell = Math.round((Date.now() - start) / 1000);
				if (dwell >= 2) trackSectionDwell(name, dwell);
			}
			enteredAt.clear();
		};
		window.addEventListener("pagehide", flush);

		return () => {
			observer.disconnect();
			window.removeEventListener("pagehide", flush);
		};
	}, []);
}
